/**
 * 管理后台 Token 本地存储与登录跳转。
 *
 * 与小程序共用同一套 /api/v1/auth 会话接口；键名独立，避免与小程序 Storage 冲突。
 */
const USER_ACCESS_TOKEN_KEY = 'mvl-system-ui.user-access-token';
const USER_REFRESH_TOKEN_KEY = 'mvl-system-ui.user-refresh-token';
const USER_ACCESS_TOKEN_EXPIRES_AT_KEY = 'mvl-system-ui.user-access-token-expires-at';
const USER_REFRESH_TOKEN_EXPIRES_AT_KEY = 'mvl-system-ui.user-refresh-token-expires-at';

const canUseStorage = () => typeof window !== 'undefined';
const LOGIN_PATH = '/user/login';

export const getUserAccessToken = () => {
  if (!canUseStorage()) {
    return undefined;
  }
  return window.localStorage.getItem(USER_ACCESS_TOKEN_KEY) ?? undefined;
};

export const getUserRefreshToken = () => {
  if (!canUseStorage()) {
    return undefined;
  }
  return window.localStorage.getItem(USER_REFRESH_TOKEN_KEY) ?? undefined;
};

export const getUserAccessTokenExpiresAt = () => {
  if (!canUseStorage()) {
    return undefined;
  }
  const value = window.localStorage.getItem(USER_ACCESS_TOKEN_EXPIRES_AT_KEY);
  return value ? Number(value) : undefined;
};

export const getUserRefreshTokenExpiresAt = () => {
  if (!canUseStorage()) {
    return undefined;
  }
  const value = window.localStorage.getItem(USER_REFRESH_TOKEN_EXPIRES_AT_KEY);
  return value ? Number(value) : undefined;
};

export const setUserTokens = (
  accessToken: string,
  refreshToken?: string,
  accessTokenExpiresInSeconds?: number,
  refreshTokenExpiresInSeconds?: number,
) => {
  if (!canUseStorage()) {
    return;
  }
  window.localStorage.setItem(USER_ACCESS_TOKEN_KEY, accessToken);
  if (accessTokenExpiresInSeconds) {
    window.localStorage.setItem(
      USER_ACCESS_TOKEN_EXPIRES_AT_KEY,
      String(Date.now() + accessTokenExpiresInSeconds * 1000),
    );
  }
  if (refreshToken) {
    window.localStorage.setItem(USER_REFRESH_TOKEN_KEY, refreshToken);
  }
  if (refreshToken && refreshTokenExpiresInSeconds) {
    window.localStorage.setItem(
      USER_REFRESH_TOKEN_EXPIRES_AT_KEY,
      String(Date.now() + refreshTokenExpiresInSeconds * 1000),
    );
  }
};

export const clearUserTokens = () => {
  if (!canUseStorage()) {
    return;
  }
  window.localStorage.removeItem(USER_ACCESS_TOKEN_KEY);
  window.localStorage.removeItem(USER_REFRESH_TOKEN_KEY);
  window.localStorage.removeItem(USER_ACCESS_TOKEN_EXPIRES_AT_KEY);
  window.localStorage.removeItem(USER_REFRESH_TOKEN_EXPIRES_AT_KEY);
};

export const isUserAccessTokenExpired = (skewSeconds: number = 30) => {
  const expiresAt = getUserAccessTokenExpiresAt();
  if (!expiresAt) {
    return false;
  }
  return expiresAt <= Date.now() + skewSeconds * 1000;
};

export const isUserRefreshTokenExpired = (skewSeconds: number = 30) => {
  const expiresAt = getUserRefreshTokenExpiresAt();
  if (!expiresAt) {
    return false;
  }
  return expiresAt <= Date.now() + skewSeconds * 1000;
};

export const mapPrimaryRoleToAccess = (primaryRole?: string) => {
  if (primaryRole === 'platform_admin') {
    return 'admin';
  }

  if (primaryRole === 'region_admin') {
    return 'region';
  }

  if (primaryRole === 'operator') {
    return 'operator';
  }

  if (primaryRole === 'village_admin') {
    return 'village';
  }

  return 'guest';
};

export const redirectToUserLogin = (redirect?: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  const currentPath = `${window.location.pathname}${window.location.search}`;
  const nextRedirect = redirect ?? currentPath;
  const currentRedirect = new URLSearchParams(window.location.search).get(
    'redirect',
  );

  if (
    window.location.pathname === LOGIN_PATH &&
    (!currentRedirect || currentRedirect === nextRedirect)
  ) {
    return;
  }

  const searchParams = new URLSearchParams();
  if (nextRedirect && nextRedirect !== LOGIN_PATH) {
    searchParams.set('redirect', nextRedirect);
  }

  window.location.href = `${LOGIN_PATH}${
    searchParams.toString() ? `?${searchParams.toString()}` : ''
  }`;
};
